import { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import { X } from "lucide-react";
import { cookiesAcceptRejectTodo, cookiesAcceptRejectRes } from "../../redux/slice/CookiesAcceptReject";
import { Link } from "react-router-dom";
import {
  CONSENT_COOKIE,
  CONSENT_VERSION,
  CONSENT_UPDATED_EVENT,
  SUBMIT_CURRENT_EVENT,
  readStoredConsent,
} from "../../utils/cookieConsent";

const COOKIE_DAYS = 180;

function setCookie(name, value, days) {
  let expires = "";
  if (days) {
    const d = new Date();
    d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
    expires = `; expires=${d.toUTCString()}`;
  }
  document.cookie = `${name}=${encodeURIComponent(value)}${expires}; path=/; SameSite=Lax`;
}

function gtag(...args) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(args);
}

function applyConsentToGoogleTags(record) {
  gtag("consent", "update", {
    analytics_storage: record.analytics ? "granted" : "denied",
    ad_storage: record.advertising ? "granted" : "denied",
    ad_user_data: record.advertising ? "granted" : "denied",
    ad_personalization: record.advertising ? "granted" : "denied",
  });
  window.dispatchEvent(new CustomEvent(CONSENT_UPDATED_EVENT, { detail: record }));
}

function persistConsent({ analytics, advertising }) {
  const record = {
    v: CONSENT_VERSION,
    necessary: true,
    analytics: !!analytics,
    advertising: !!advertising,
    ip_disclosed: true,
    ts: new Date().toISOString(),
  };
  setCookie(CONSENT_COOKIE, JSON.stringify(record), COOKIE_DAYS);
  try {
    localStorage.setItem(CONSENT_COOKIE, JSON.stringify(record));
  } catch {
    /* ignore */
  }
  applyConsentToGoogleTags(record);
  return record;
}

function Toggle({ checked, disabled, onChange, label }) {
  return (
    <label style={styles.toggleWrap}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange && onChange(e.target.checked)}
        style={styles.srOnly}
        aria-label={label}
      />
      <div
        style={{
          ...styles.toggleTrack,
          background: disabled ? "#bfdbfe" : checked ? "#2563eb" : "#d1d5db",
          cursor: disabled ? "default" : "pointer",
        }}
      >
        <div
          style={{
            ...styles.toggleThumb,
            transform: checked ? "translateX(16px)" : "translateX(0)",
          }}
        />
      </div>
    </label>
  );
}

export default function CookieConsentBanner() {
  const dispatch = useDispatch();
  const [status, setStatus] = useState("checking");
  const [showModal, setShowModal] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [advertising, setAdvertising] = useState(false);
  const [loading, setLoading] = useState(false);
  const analyticsRef = useRef(analytics);
  const advertisingRef = useRef(advertising);
  const loadingRef = useRef(loading);
  analyticsRef.current = analytics;
  advertisingRef.current = advertising;
  loadingRef.current = loading;

  useEffect(() => {
    gtag("consent", "default", {
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
      analytics_storage: "denied",
      wait_for_update: 500,
    });

    const existing = readStoredConsent();
    if (existing) {
      applyConsentToGoogleTags(existing);
      setAnalytics(existing.analytics);
      setAdvertising(existing.advertising);
      setStatus("done");
    } else {
      setStatus("prompt");
    }
  }, []);

  const save = useCallback(async (prefs, apiStatus) => {
    if (loading) return;
    setLoading(true);
    try {
      await dispatch(cookiesAcceptRejectTodo({
        status: apiStatus,
        preferences: {
          Strictly: true,
          Analytics: !!prefs.analytics,
          Advertising: !!prefs.advertising,
        },
      }));
      const uniqueId = cookiesAcceptRejectRes?.data?.data?.[0]?.unique_id;
      if (uniqueId) {
        try {
          localStorage.setItem('unique_id', uniqueId);
        } catch {
          /* ignore */
        }
      }
      const record = persistConsent(prefs);
      setAnalytics(record.analytics);
      setAdvertising(record.advertising);
      setShowModal(false);
      setStatus("done");
    } finally {
      setLoading(false);
    }
  }, [dispatch, loading]);

  const acceptAll = () => save({ analytics: true, advertising: true }, "Accepted");
  const rejectAll = () => save({ analytics: false, advertising: false }, "Rejected");
  const savePrefs = useCallback(() => {
    const nextAnalytics = analyticsRef.current;
    const nextAdvertising = advertisingRef.current;
    return save(
      { analytics: nextAnalytics, advertising: nextAdvertising },
      nextAnalytics || nextAdvertising ? "Accepted" : "Rejected"
    );
  }, [save]);

  useEffect(() => {
    const onSubmitCurrent = () => {
      const existing = readStoredConsent();
      if (existing) {
        window.dispatchEvent(new CustomEvent(CONSENT_UPDATED_EVENT, { detail: existing }));
        return;
      }
      if (loadingRef.current) return;
      savePrefs();
    };
    window.addEventListener(SUBMIT_CURRENT_EVENT, onSubmitCurrent);
    return () => window.removeEventListener(SUBMIT_CURRENT_EVENT, onSubmitCurrent);
  }, [savePrefs]);

  return (
    <>
      {status === "prompt" && (
        <div role="dialog" aria-label="Cookie consent" style={styles.banner}>
          <p style={styles.bannerText}>
          We use cookies to run this site, understand traffic through Analytics Cookies and personalize ads through Advertising Cookies. This can improve your overall experience and personalize ads. You can accept all, reject non‑essential cookies, or manage your preferences.{" "}Visit
            {/* <a href="/privacy-policy" style={styles.link} target="_blank" rel="noopener noreferrer">
              Privacy &amp; Cookie Policy
            </a> */}
            <strong> <Link to='/privacy-policy' target="_blank" style={styles.link}><span className='text-primary'> Privacy Policy</span></Link> & <Link to='/cookies-policy' target="_blank" style={styles.link}><span className='text-primary'> Cookie Policy </span> </Link> </strong>
            for more information.
          </p>
          <div style={styles.bannerActions}>
            <button type="button" onClick={() => setShowModal(true)} style={styles.linkBtn}>
              Manage Preferences
            </button>
            <button type="button" onClick={rejectAll} disabled={loading} style={styles.outlineBtnDark}>
              Reject Non-Essential
            </button>
            <button type="button" onClick={acceptAll} disabled={loading} style={styles.primaryBtn}>
              Accept All
            </button>
          </div>
        </div>
      )}

      {showModal && (
        <div
          style={styles.overlay}
          onClick={(e) => e.target === e.currentTarget && setShowModal(false)}
        >
          <div role="dialog" aria-modal="true" aria-labelledby="cc-modal-title" style={styles.modal}>
            <div style={styles.modalHeader}>
              <h2 id="cc-modal-title" style={styles.modalTitle}>
                Manage Cookie Preferences
              </h2>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                aria-label="Close"
                style={styles.closeBtn}
              >
                <X size={20} />
              </button>
            </div>

            <div style={styles.modalBody}>
              <p style={styles.muted}>
                Choose which categories of cookies we may use. Necessary cookies are always
                active. Your choice Will be saved for {COOKIE_DAYS} days.
              </p>

              <div style={styles.card}>
                <div style={styles.cardRow}>
                  <strong style={styles.cardTitle}>Necessary Cookies</strong>
                  <Toggle checked={true} disabled label="Necessary Cookies" />
                </div>
                <p style={styles.cardDesc}>
                  Required for core site functionality, security, and session handling. Cannot
                  be turned off.
                </p>
              </div>

              <div style={styles.card}>
                <div style={styles.cardRow}>
                  <strong style={styles.cardTitle}>Analytics Cookies</strong>
                  <Toggle checked={analytics} onChange={setAnalytics} label="Analytics cookies" />
                </div>
                <p style={styles.cardDesc}>
                  Helps us understand how visitors use the site (pages viewed, session length,
                  general location).
                  <span style={styles.italicNote}>
                    Your IP address is collected as part of this to identify approximate
                    location and prevent abuse.
                  </span>
                </p>
              </div>

              <div style={styles.card}>
                <div style={styles.cardRow}>
                  <strong style={styles.cardTitle}>Advertising Cookies</strong>
                  <Toggle
                    checked={advertising}
                    onChange={setAdvertising}
                    label="Advertising cookies"
                  />
                </div>
                <p style={styles.cardDesc}>
                  User to show and measure relevant ads.
                  <span style={styles.italicNote}>
                    Your IP address is used to serve ads and measure their performance.
                  </span>
                </p>
              </div>
            </div>

            <div style={styles.modalFooter}>
              <button type="button" onClick={rejectAll} disabled={loading} style={styles.outlineBtnLight}>
                Reject Non-Essential
              </button>
              <button type="button" onClick={savePrefs} disabled={loading} style={styles.outlineBtnLight}>
                Save and Accept
              </button>
              <button type="button" onClick={acceptAll} disabled={loading} style={styles.primaryBtn}>
                Accept All
              </button>
            </div>
          </div>
        </div>
      )}

    </>
  );
}

const styles = {
  banner: {
    position: "fixed",
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 2147483647,
    background: "#111827",
    color: "#f3f4f6",
    padding: "16px 20px",
    boxShadow: "0 -8px 30px rgba(0,0,0,0.25)",
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
  },
  bannerText: {
    flex: 1,
    minWidth: 280,
    maxWidth: 768,
    fontSize: 14,
    lineHeight: 1.6,
    margin: 0,
  },
  link: {
    color: "#60a5fa",
    textDecoration: "underline",
  },
  bannerActions: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
  },
  linkBtn: {
    fontSize: 14,
    color: "#d1d5db",
    textDecoration: "underline",
    background: "transparent",
    border: "none",
    padding: "8px 4px",
    cursor: "pointer",
  },
  outlineBtnDark: {
    fontSize: 14,
    fontWeight: 600,
    border: "1px solid #4b5563",
    borderRadius: 8,
    padding: "8px 16px",
    background: "transparent",
    color: "#f3f4f6",
    cursor: "pointer",
  },
  outlineBtnLight: {
    fontSize: 14,
    fontWeight: 600,
    border: "1px solid #d1d5db",
    borderRadius: 8,
    padding: "8px 16px",
    background: "#fff",
    color: "#111827",
    cursor: "pointer",
  },
  primaryBtn: {
    fontSize: 14,
    fontWeight: 600,
    background: "#2563eb",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    padding: "8px 16px",
    cursor: "pointer",
  },
  overlay: {
    position: "fixed",
    inset: 0,
    zIndex: 2147483647,
    background: "rgba(0,0,0,0.55)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
  },
  modal: {
    background: "#fff",
    color: "#111827",
    width: "100%",
    maxWidth: 448,
    borderRadius: 12,
    maxHeight: "86vh",
    overflowY: "auto",
    boxShadow: "0 25px 50px rgba(0,0,0,0.35)",
  },
  modalHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "20px 24px",
    borderBottom: "1px solid #f3f4f6",
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 600,
    margin: 0,
  },
  closeBtn: {
    background: "transparent",
    border: "none",
    color: "#9ca3af",
    cursor: "pointer",
    padding: 4,
  },
  modalBody: {
    padding: "20px 24px",
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  muted: {
    fontSize: 14,
    color: "#6b7280",
    margin: 0,
  },
  card: {
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    padding: 16,
  },
  cardRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  cardTitle: {
    fontSize: 14,
  },
  cardDesc: {
    fontSize: 14,
    color: "#6b7280",
    margin: "6px 0 0",
  },
  italicNote: {
    display: "block",
    fontStyle: "italic",
    color: "#9ca3af",
    marginTop: 4,
  },
  modalFooter: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "flex-end",
    gap: 8,
    padding: "16px 24px",
    borderTop: "1px solid #f3f4f6",
  },
  toggleWrap: {
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
    flexShrink: 0,
  },
  srOnly: {
    position: "absolute",
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: "hidden",
    clip: "rect(0,0,0,0)",
    whiteSpace: "nowrap",
    border: 0,
  },
  toggleTrack: {
    width: 40,
    height: 24,
    borderRadius: 999,
    transition: "background 0.2s",
  },
  toggleThumb: {
    height: 20,
    width: 20,
    marginTop: 2,
    marginLeft: 2,
    background: "#fff",
    borderRadius: "50%",
    boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
    transition: "transform 0.2s",
  },
};
